import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { apiService } from '../../services/apiService';
import { getVendorLabel, getVendorIcon, capabilitiesForType } from '../Integrations/agentMeta';
import { getIndicatorErrorMessage } from './IndicatorDialog';

const idOf = (agent) => agent.id || agent._id;

/**
 * Dialog to push one or more indicators to EDR integrations that support
 * indicator/IOC management (CrowdStrike, SentinelOne, Microsoft Defender).
 *
 * Pass either `indicatorId` (single indicator, uses POST /indicators/:id/push
 * and returns the updated indicator) or `indicatorIds` (bulk, uses POST
 * /indicators/push and only returns a summary).
 */
const PushToEdrDialog = ({ open, onClose, indicatorId, indicatorIds, onPushed }) => {
  const [agents, setAgents] = useState([]);
  const [types, setTypes] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [action, setAction] = useState('detect');
  const [pushing, setPushing] = useState(false);
  const [pushError, setPushError] = useState('');
  const [result, setResult] = useState(null);

  const isSingle = !!indicatorId;
  const count = isSingle ? 1 : (indicatorIds || []).length;

  const eligibleAgents = useMemo(
    () => agents.filter((a) => a.isEnabled && capabilitiesForType(a.type, types).iocManagement),
    [agents, types]
  );

  useEffect(() => {
    if (!open) return;
    setPushError('');
    setResult(null);
    setAction('detect');
    let cancelled = false;
    setLoadingAgents(true);
    setLoadError('');
    Promise.all([apiService.agents.getAll(), apiService.agents.getTypes()])
      .then(([agentsRes, typesRes]) => {
        if (cancelled) return;
        const loadedAgents = agentsRes.data.agents || [];
        const loadedTypes = typesRes.data.types || [];
        setAgents(loadedAgents);
        setTypes(loadedTypes);
        const eligible = loadedAgents.filter((a) => a.isEnabled && capabilitiesForType(a.type, loadedTypes).iocManagement);
        setSelectedAgentIds(eligible.map(idOf));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load agents for EDR push:', err);
        setLoadError('Failed to load EDR integrations');
      })
      .finally(() => { if (!cancelled) setLoadingAgents(false); });
    return () => { cancelled = true; };
  }, [open]);

  const toggleAgent = (agentId, checked) => {
    setSelectedAgentIds((ids) => (checked ? [...ids, agentId] : ids.filter((id) => id !== agentId)));
  };

  const handlePush = async () => {
    if (selectedAgentIds.length === 0) return;
    try {
      setPushing(true);
      setPushError('');
      const res = isSingle
        ? await apiService.indicators.pushOne(indicatorId, { agentIds: selectedAgentIds, action })
        : await apiService.indicators.push(indicatorIds, { agentIds: selectedAgentIds, action });
      setResult(res.data);
      if (onPushed) onPushed(res.data);
    } catch (err) {
      console.error('Failed to push indicator(s) to EDR:', err);
      setPushError(getIndicatorErrorMessage(err, 'Failed to push to EDR'));
    } finally {
      setPushing(false);
    }
  };

  const handleClose = () => {
    if (pushing) return;
    onClose();
  };

  const agentSummaries = result?.agents || [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Push to EDR</DialogTitle>
      <DialogContent>
        {!result && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Deploy {count === 1 ? 'this indicator' : `${count} indicators`} as a custom IOC to the selected EDR integrations.
          </Typography>
        )}

        {loadingAgents ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : result ? (
          <List dense disablePadding>
            {agentSummaries.map((a) => {
              const matchingAgent = agents.find((ag) => idOf(ag) === a.agentId);
              return (
              <ListItem key={a.agentId} alignItems="flex-start" disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>{getVendorIcon(matchingAgent?.type, { fontSize: 'small' })}</ListItemIcon>
                <ListItemText
                  primary={(
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{a.agentName}</Typography>
                      <Chip
                        size="small"
                        label={a.skipped ? 'Skipped' : a.success ? 'Success' : 'Failed'}
                        color={a.skipped ? 'default' : a.success ? 'success' : 'error'}
                        variant={a.skipped ? 'outlined' : 'filled'}
                      />
                    </Box>
                  )}
                  secondary={
                    a.error
                      || (a.results && a.results.length > 1
                        ? `${a.results.filter((r) => r.success).length}/${a.results.length} indicators pushed`
                        : (a.results && a.results[0] && a.results[0].error) || undefined)
                  }
                />
              </ListItem>
              );
            })}
            {agentSummaries.length === 0 && (
              <Typography variant="body2" color="text.secondary">No eligible agents were selected.</Typography>
            )}
          </List>
        ) : eligibleAgents.length === 0 ? (
          <Alert severity="info">No enabled integration supports indicator management (CrowdStrike, SentinelOne and Microsoft Defender are supported).</Alert>
        ) : (
          <>
            {pushError && <Alert severity="error" sx={{ mb: 2 }}>{pushError}</Alert>}
            <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
              <FormLabel component="legend">Integrations</FormLabel>
              <FormGroup>
                {eligibleAgents.map((agent) => {
                  const id = idOf(agent);
                  return (
                    <FormControlLabel
                      key={id}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedAgentIds.includes(id)}
                          onChange={(e) => toggleAgent(id, e.target.checked)}
                        />
                      }
                      label={(
                        <Box display="flex" alignItems="center" gap={1}>
                          {getVendorIcon(agent.type, { fontSize: 'small' })}
                          <Typography variant="body2">{agent.name}</Typography>
                          <Typography variant="caption" color="text.secondary">({getVendorLabel(agent.type, types)})</Typography>
                        </Box>
                      )}
                    />
                  );
                })}
              </FormGroup>
            </FormControl>

            <Divider sx={{ mb: 2 }} />

            <FormControl component="fieldset">
              <FormLabel component="legend">Action</FormLabel>
              <RadioGroup row value={action} onChange={(e) => setAction(e.target.value)}>
                <FormControlLabel value="detect" control={<Radio size="small" />} label="Detect" />
                <FormControlLabel value="block" control={<Radio size="small" />} label="Block" />
              </RadioGroup>
              <Typography variant="caption" color="text.secondary">
                Detect only alerts on a match. Block also prevents execution on CrowdStrike and Microsoft Defender; SentinelOne only
                supports detect-style IOCs, so a block request is applied there as detect.
              </Typography>
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button onClick={onClose} variant="contained">Done</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={pushing}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handlePush}
              disabled={pushing || loadingAgents || !!loadError || eligibleAgents.length === 0 || selectedAgentIds.length === 0}
            >
              {pushing ? <CircularProgress size={20} /> : 'Push'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PushToEdrDialog;
